import React, { useEffect, useState } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { useApi } from '../../services/api';

type DependencyHealth = {
  displayName: string;
  active: number;
  configuredLimit: number | null;
  remainingHeadroom: number | null;
  monthlyUsage: number;
  monthlyAllowance: number | null;
  monthlyUnit: 'characters' | 'minutes';
  usageSource: 'measured_intellerview' | 'planning_estimate' | 'not_available';
};

type CapacityHealth = {
  activeCapacityUnits: number;
  activeInterviews: number;
  capacity: {
    dependencies: Record<'livekit' | 'deepgram' | 'cartesia' | 'elevenlabs', DependencyHealth>;
    currentHardBottleneck: string | null;
    monthlyUsageStatus: 'Normal' | 'Warning' | 'Critical';
    providerReportedBalancesIncluded: false;
  };
};

const concurrency = (dependency: DependencyHealth) =>
  dependency.configuredLimit === null
    ? `${dependency.active} / limit not configured`
    : `${dependency.active} / ${dependency.configuredLimit}`;

const OwnedAvatarCapacityStatusPanel: React.FC = () => {
  const session = useSession();
  const api = useApi();
  const isJason = session?.user?.email?.trim().toLowerCase() === 'jason@intellerview.com';
  const [health, setHealth] = useState<CapacityHealth | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    if (!isJason) return;
    let active = true;
    void api.get<{ result: CapacityHealth }>('/ai-interviews/owned-avatar-demo/monitoring/health')
      .then((response) => {
        if (active) setHealth(response.data.result);
      })
      .catch((error: any) => {
        if (active) setErrorCode(error?.response?.data?.code || 'capacity_status_unavailable');
      });
    return () => { active = false; };
  }, [api, isJason]);

  if (!isJason) return null;
  const capacity = health?.capacity;
  const dependencies = capacity?.dependencies;
  const bottleneck = capacity?.currentHardBottleneck;
  const bottleneckLabel = bottleneck && dependencies
    ? dependencies[bottleneck as keyof typeof dependencies]?.displayName || bottleneck
    : 'Not available';
  const rows = [
    ['Active interviews', health ? String(health.activeInterviews) : 'Loading'],
    ['LiveKit', dependencies ? concurrency(dependencies.livekit) : 'Loading'],
    ['Deepgram', dependencies ? concurrency(dependencies.deepgram) : 'Loading'],
    ['Cartesia', dependencies ? concurrency(dependencies.cartesia) : 'Loading'],
    ['ElevenLabs (no production profile)', dependencies ? concurrency(dependencies.elevenlabs) : 'Loading'],
    ['Active GPUs', health ? String(health.activeCapacityUnits) : 'Loading'],
    ['Current hard bottleneck', bottleneckLabel],
    ['Monthly usage', capacity?.monthlyUsageStatus || 'Loading'],
  ];

  return (
    <section
      aria-label="Owned Avatar capacity status"
      className="rounded-[8px] border border-[#bae6fd] bg-[#f0f9ff] px-5 py-4 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0369a1]">Owned Avatar operations</p>
          <h2 className="mt-1 text-[16px] font-bold text-[#0f172a]">Current capacity</h2>
        </div>
        <span className="rounded-full border border-[#7dd3fc] bg-white px-2.5 py-1 text-[11px] font-bold text-[#075985]">
          Jason-only
        </span>
      </div>
      {errorCode ? <p className="mt-3 text-[12px] text-[#991b1b]">Capacity unavailable ({errorCode}).</p> : null}
      <dl className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
        {rows.map(([label, value]) => (
          <div key={label} className="rounded-[6px] border border-[#bae6fd] bg-white px-3 py-2">
            <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#64748b]">{label}</dt>
            <dd className="mt-1 text-[12px] font-semibold text-[#334155]">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-[11px] text-[#64748b]">
        All four production profiles use Cartesia; ElevenLabs remains available in code only. Monthly figures are measured Intellerview usage or clearly labelled planning estimates; provider account balances are not inferred.
      </p>
    </section>
  );
};

export default OwnedAvatarCapacityStatusPanel;
